<script lang="ts">
    import { appManager } from "../AppManagerStore";
    import Head from "../header/Head.svelte";
    import Navbar from "../header/Navbar.svelte";
    import type { Post } from "./Post";
    import { onMount } from "svelte";
    import type { Author } from "./Author";
    import moment from "moment";
    import Footer from "../footer/Footer.svelte";
    import { _, locale } from "svelte-i18n";
    import { Alert } from "sveltestrap";

    export let id: string;

    let post: Post;

    let formattedDate: string = "";
    let url = "";
    let html = "";
    let title = "";
    let tagline = "";
    let tags: string[] = [];
    let image = "";
    let localeNotAvailable = false;

    let author: {
        photo: string;
        name: string;
        twitter?: string;
        github?: string;
        linkedin?: string;
    } = {
        photo: "",
        name: "",
    };

    onMount(() => (url = window.location.href));

    locale.subscribe((ln) => {
        if (typeof post !== "undefined") {
            getPost();
        }
    });

    async function load(): Promise<boolean> {
        const response = await fetch(`${$appManager.PublicUrl}data/blog/${id}.json`);

        post = (await response.json()) as Post;
        formattedDate = moment(post.date).format("MMMM YYYY");
        image = post.image;

        getPost();

        const authorsResponse = await fetch(
            `${$appManager.PublicUrl}data/blog/${post.author}.json`
        );
        author = (await authorsResponse.json()) as Author;

        return true;
    }

    function getPost() {
        let translation = post.translations[post.lang];
        title = translation.title;
        html = translation.html;
        tagline = translation.tagline;
        tags = translation.tags;

        if (typeof post.translations[$locale] !== "undefined") {
            translation = post.translations[$locale];
            if (translation.html?.length <= 0) {
                // there is no html, there is no translated content.
                title = translation.title;
                tagline = translation.tagline;
                tags = translation.tags;
                localeNotAvailable = true;
            } else {
                title = translation.title;
                html = translation.html;
                tagline = translation.tagline;
                tags = translation.tags;
            }
        } else {
            localeNotAvailable = true;
        }
    }
</script>

<svelte:head>
    <title>ANYWAYS | Blog | {title}</title>

    <meta property="og:title" content={title} />
    <meta property="og:type" content="article" />
    <meta property="og:url" content={window.location.href} />
    <meta property="og:image" content={$appManager.PublicUrl}{image} />

    <meta name="twitter:title" content={title} />
    <meta
        name="twitter:image"
        content={$appManager.PublicUrl}{image} />
    <meta name="twitter:card" content="summary_large_image" />
</svelte:head>

<Navbar isDark={false} isFluid={false} />

{#await load()}
    <div />
{/await}

{#if localeNotAvailable}
    <Alert class="rounded-0 w-100 mb-0" dismissible>
        <div class="w-100 text-center">
            {$_("blogpost.localenotavailable")}
            <a
                href="http://translate.google.com/translate?js=n&sl=auto&tl={$locale}&u={window
                    .location.href}">{$_("blogpost.here")}</a
            >.
        </div>
    </Alert>
{/if}

<div class="row px-4">
    <div class="col-12 pt-md-10 order-1">
        <p class="text-center text-muted mb-4 mb-lg-6">
            {#each tags as tag}
                <span class="badge bg-secondary me-1">{tag}</span>
            {/each}
        </p>

        <!-- Heading -->
        <h1 class="display-3 text-center">
            {title}
        </h1>

        <!-- Text -->
        <p class="lead text-center text-muted">
            {tagline}
        </p>

        <!-- Text -->
        <p class="lead text-center text-muted">{$_("blogpost.by")}</p>

        <div class="text-center">
            <div class="us-container">
                <div class="avatar avatar-xxl mb-3">
                    <img
                        src="{$appManager.PublicUrl}{author.photo}"
                        alt="..."
                        class="avatar-img rounded-circle" />
                </div>
                <h4 class="card-title font-weight-bold us-header">
                    {author.name}
                </h4>

                <div class="mx-auto row m-3 mb-8" style="max-width: 120px;">
                    <div class="mx-auto" style="width: 120px;">
                        {#if typeof author.twitter !== "undefined"}
                            <a
                                href="https://twitter.com/{author.twitter}"
                                class="m-1 text-decoration-none">
                                <img
                                    src="{$appManager.PublicUrl}img/icons/social/twitter.svg"
                                    class="list-social-icon"
                                    alt="..." />
                            </a>
                        {/if}

                        {#if typeof author.github !== "undefined"}
                            <a
                                href="author.github"
                                class="m-1 text-decoration-none">
                                <img
                                    src="{$appManager.PublicUrl}img/icons/social/github.svg"
                                    class="list-social-icon"
                                    alt="..." />
                            </a>
                        {/if}

                        {#if typeof author.linkedin !== "undefined"}
                            <a
                                href={author.linkedin}
                                class="m-1 text-decoration-none">
                                <img
                                    src="{$appManager.PublicUrl}img/icons/social/linkedin.svg"
                                    class="list-social-icon"
                                    alt="..." />
                            </a>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
<div class="row px-4">
    <img
        src="{$appManager.PublicUrl}{image}"
        class="mx-auto img-fluid"
        style="max-width: 1000px;"
        alt="..." />
</div>
<div class="row pt-8 px-4">
    <div class="mx-auto font-size-lg" style="max-width: 800px;">
        <div class="post-content">
            {@html html}

            <p class="font-size-sm text-muted ml-2">
                {$_("blogpost.postedby")}
                {author.name}, {formattedDate}
            </p>
        </div>

        <h5 class="text-center text-muted">Liked this article? Share it.</h5>
        <div class="mx-auto row m-3 mb-8" style="max-width: 120px;">
            <div class="mx-auto" style="width: 120px;">
                <a
                    href="https://twitter.com/share?url={url}"
                    class="m-1 text-decoration-none">
                    <img
                        src="{$appManager.PublicUrl}img/icons/social/twitter.svg"
                        class="list-social-icon"
                        alt="..." />
                </a>

                <a
                    href="https://www.linkedin.com/shareArticle?url={url}"
                    class="m-1 text-decoration-none">
                    <img
                        src="{$appManager.PublicUrl}img/icons/social/linkedin.svg"
                        class="list-social-icon"
                        alt="..." />
                </a>
            </div>
        </div>
    </div>
</div>

<Footer />

<style>
</style>
